import React from 'react'

function formatDate(dateStr) {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${parseInt(month)}/${parseInt(day)}/${year}`
}

function fileKind(contentType) {
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType?.startsWith('image/')) return 'IMG'
  return 'FILE'
}

export default function MaintenanceTimeline({ visits, meterLabel = 'mi', onEdit, onDelete, onDismissOverdue }) {
  if (!visits || visits.length === 0) {
    return <p className="text-muted text-sm py-8">No maintenance visits yet. Add the first one to start the log.</p>
  }

  return (
    <div>
      {visits.map((visit) => (
        <div
          key={visit.id}
          className="grid md:grid-cols-[190px_minmax(0,1fr)_auto] gap-x-6 gap-y-3 py-5 border-b border-line"
        >
          <div className="flex flex-wrap md:block items-baseline gap-x-4 gap-y-0.5">
            {visit.service_date && (
              <p className="font-mono font-semibold text-ink">{formatDate(visit.service_date)}</p>
            )}
            {visit.meter_at_service && (
              <p className="font-mono text-sm text-muted md:mt-1">{visit.meter_at_service.toLocaleString()} {meterLabel}</p>
            )}
            {visit.shop_name && (
              <p className="text-sm text-muted md:mt-1">{visit.shop_name}</p>
            )}
          </div>

          <div className="min-w-0">
            <div className="space-y-2.5">
              {visit.service_entries.map((entry) => (
                <div key={entry.id} className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-bold text-ink">{entry.title}</span>
                      {entry.overdue && (
                        <span className="inline-flex items-center gap-2">
                          <span className="bg-accent text-white text-[10px] font-extrabold tracking-widest px-1.5 py-0.5">OVERDUE</span>
                          {onDismissOverdue && (
                            <button
                              type="button"
                              onClick={() => onDismissOverdue(entry.id)}
                              className="text-xs font-semibold text-muted hover:text-ink py-1"
                            >
                              Dismiss
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <p className="text-sm text-muted">{entry.description}</p>
                    )}
                    {(entry.next_service_date || entry.next_service_meter) && (
                      <p className="text-xs text-muted mt-0.5">
                        Next:
                        {entry.next_service_date && ` by ${formatDate(entry.next_service_date)}`}
                        {entry.next_service_meter && ` at ${entry.next_service_meter.toLocaleString()} ${meterLabel}`}
                      </p>
                    )}
                  </div>
                  {entry.cost > 0 && (
                    <span className="font-mono text-sm text-ink shrink-0">${entry.cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              ))}
            </div>

            {visit.files && visit.files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {visit.files.map((file) => (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-ink border border-ink px-2 py-1 hover:bg-white max-w-full"
                  >
                    <span className="font-mono text-[10px] text-muted">{fileKind(file.content_type)}</span>
                    <span className="truncate max-w-[180px]">{file.filename}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="flex md:flex-col items-center md:items-end justify-between md:justify-start gap-2">
            {visit.total_cost > 0 && visit.service_entries.length > 1 && (
              <div className="md:text-right">
                <p className="hidden md:block text-[11px] font-bold uppercase tracking-widest text-muted">Total</p>
                <p className="font-mono text-lg font-semibold text-ink">${visit.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
            )}
            {(onEdit || onDelete) && (
              <div className="flex gap-4 text-sm font-semibold">
                {onEdit && (
                  <button onClick={() => onEdit(visit)} className="text-ink hover:underline underline-offset-4 py-1">
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(visit.id)} className="text-muted hover:text-accent py-1">
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
